import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.canUpdateQuantity !== 'false') {
      this.serverDisabledMinus = false;
      this.serverDisabledPlus = false;
      this.updateButtonStates();
    }
  }

  /**
   * Whether the selector should remove the line item when the minimum quantity is reached.
   * @returns {boolean}
   */
  get removeAtMin() {
    return this.dataset.removeAtMin === 'true';
  }

  /**
   * Whether the current line item is removable.
   * @returns {boolean}
   */
  get canRemoveAtMin() {
    return this.dataset.canRemove !== 'false';
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const isRemoveState = this.removeAtMin && value <= min;

    this.updateMinusButtonState(isRemoveState);

    if (!this.serverDisabledMinus) {
      minusButton.disabled = isRemoveState ? value < min || !this.canRemoveAtMin : value <= min;
    }

    if (!this.serverDisabledPlus) {
      plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
  }

  /**
   * Handles the quantity decrease event.
   * At minimum quantity, this optionally removes the line item instead.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const { min, value } = this.getCurrentValues();

    if (this.removeAtMin && value <= min) {
      if (this.serverDisabledMinus || !this.canRemoveAtMin) return;

      // No updateButtonStates() here: it would disable the focused button and
      // drop focus to <body> before the cart can move it to the next line.
      this.refs.quantityInput.value = '0';
      this.onQuantityChange();
      return;
    }

    this.updateQuantity(-1);
  }

  /**
   * Syncs the minus button icon and accessible label with the current mode.
   * @param {boolean} isRemoveState
   */
  updateMinusButtonState(isRemoveState) {
    if (!this.removeAtMin) return;

    const { minusButton } = this.refs;
    const label = minusButton.querySelector('.quantity-selector__minus-label');
    const minusIcon = minusButton.querySelector('.quantity-selector__icon--minus');
    const deleteIcon = minusButton.querySelector('.quantity-selector__icon--delete');
    const decreaseLabel = this.dataset.decreaseLabel;
    const removeLabel = this.dataset.removeLabel;
    const activeLabel = isRemoveState ? removeLabel : decreaseLabel;

    minusButton.dataset.mode = isRemoveState ? 'remove' : 'decrease';

    if (activeLabel) {
      minusButton.setAttribute('aria-label', activeLabel);
      if (label) {
        label.textContent = activeLabel;
      }
    }

    if (minusIcon instanceof HTMLElement) {
      minusIcon.hidden = isRemoveState;
    }

    if (deleteIcon instanceof HTMLElement) {
      deleteIcon.hidden = !isRemoveState;
    }
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
